import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, Star, MapPin, Utensils } from "lucide-react";

export default function HomePage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [cuisine, setCuisine] = useState("all");
    const [price, setPrice] = useState("all");

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                setLoading(true);
                const { data } = await api.get("/restaurants");
                if (active) setList(data || []);
            } catch (error) {
                console.warn("[home] load failed", error?.message);
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => {
            active = false;
        };
    }, []);

    const cuisines = useMemo(() => Array.from(new Set(list.map(r => r.cuisine))).sort(), [list]);

    const filtered = useMemo(() => list.filter(r => {
        if (cuisine !== "all" && r.cuisine !== cuisine) return false;
        if (price !== "all" && r.price_range !== price) return false;
        if (q) {
            const s = q.toLowerCase();
            if (!`${r.name} ${r.cuisine} ${r.description} ${r.address}`.toLowerCase().includes(s)) return false;
        }
        return true;
    }), [list, q, cuisine, price]);

    return (
        <div className="relative">
            <section className="warm-linen-wash noise">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 pt-10 pb-8">
                    <div className="flex items-end justify-between flex-wrap gap-3">
                        <div>
                            <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground mb-2">Good {timeOfDay()}, {user?.name?.split(" ")[0] || "there"}</div>
                            <h1 className="font-display text-4xl sm:text-5xl tracking-tight">Tonight's table is waiting.</h1>
                            <p className="mt-2 text-muted-foreground max-w-xl">Discover restaurants near you, reserve in seconds, and step into a perfectly orchestrated evening.</p>
                        </div>
                    </div>
                    <div className="mt-6 grid sm:grid-cols-[1fr_auto_auto] gap-2 max-w-3xl">
                        <div className="relative">
                            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search restaurants, cuisines, neighborhoods…"
                                className="pl-9 h-11 bg-card" data-testid="restaurant-search-input" />
                        </div>
                        <Select value={cuisine} onValueChange={setCuisine}>
                            <SelectTrigger className="h-11 sm:w-44" data-testid="restaurant-filter-cuisine-select">
                                <SelectValue placeholder="Cuisine" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All cuisines</SelectItem>
                                {cuisines.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Select value={price} onValueChange={setPrice}>
                            <SelectTrigger className="h-11 sm:w-36" data-testid="restaurant-filter-price-select">
                                <SelectValue placeholder="Price" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Any price</SelectItem>
                                <SelectItem value="$">$</SelectItem>
                                <SelectItem value="$$">$$</SelectItem>
                                <SelectItem value="$$$">$$$</SelectItem>
                                <SelectItem value="$$$$">$$$$</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            </section>

            <section className="mx-auto max-w-7xl px-4 sm:px-6 py-8">
                <div className="flex items-baseline justify-between mb-4">
                    <h2 className="font-display text-2xl">Featured tonight</h2>
                    <span className="text-xs text-muted-foreground">{filtered.length} of {list.length} restaurants</span>
                </div>
                {loading ? (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <Card key={`skel-${i}`} className="overflow-hidden">
                                <Skeleton className="h-48 w-full" />
                                <CardContent className="p-4 space-y-2">
                                    <Skeleton className="h-5 w-2/3" />
                                    <Skeleton className="h-4 w-1/2" />
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                ) : filtered.length === 0 ? (
                    <Card>
                        <CardContent className="p-10 text-center">
                            <Utensils className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
                            <p className="font-medium">No restaurants match your filters</p>
                            <p className="text-sm text-muted-foreground">Try clearing search or changing filters.</p>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                            {filtered.map((r) => (
                                <Card key={r.id} className="group overflow-hidden rounded-xl border-border bg-card shadow-sm hover:shadow-md" data-testid="restaurant-card">
                                <AspectRatio ratio={16 / 10}>
                                    <div className="relative h-full w-full overflow-hidden">
                                        <img src={r.image_url} alt={r.name}
                                            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" />
                                        <div className="absolute inset-x-0 top-0 h-1/3 bg-gradient-to-b from-black/30 to-transparent" />
                                        <div className="absolute top-3 left-3 flex gap-2">
                                            <Badge className="bg-[hsl(var(--brand-gold)/0.95)] text-[hsl(var(--brand-ink))] border-0">
                                                <Star className="h-3 w-3 mr-1 fill-current" /> {r.rating?.toFixed?.(1) || r.rating}
                                            </Badge>
                                            <Badge variant="secondary" className="bg-white/85 text-foreground border-0">{r.price_range}</Badge>
                                        </div>
                                    </div>
                                </AspectRatio>
                                <CardContent className="p-4">
                                    <div className="flex items-start justify-between gap-2">
                                        <div>
                                            <h3 className="font-display text-xl leading-tight">{r.name}</h3>
                                            <div className="text-xs text-muted-foreground mt-0.5">{r.cuisine} · {r.review_count} reviews</div>
                                        </div>
                                    </div>
                                    <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{r.description}</p>
                                    <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
                                        <MapPin className="h-3.5 w-3.5" />
                                        <span className="truncate">{r.address}</span>
                                    </div>
                                    <Button className="w-full mt-4" onClick={() => navigate(`/restaurants/${r.id}`)} data-testid="restaurant-card-reserve-button">
                                        Reserve a table
                                    </Button>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}

function timeOfDay() {
    const h = new Date().getHours();
    if (h < 12) return "morning";
    if (h < 17) return "afternoon";
    return "evening";
}
